/**
 * Call Tracking: All Call Tracking pages
 * - Call Logs
 * - Text Messages
 * - Active Number Summary
 * - Forwarding Numbers
 * - Non-Syndicated Numbers
 * - Call and Text Settings
 *
 * IVR translations live in ./ivr.ts; Call Groups translations live in ./call-groups.ts.
 */
export const callTrackingEn = {
  callTracking: {
    stats: {
      calls: 'Calls',
      textMessagesReceived: 'Text Messages Received',
      avgCallTime: 'Avg. Call Time',
      callTrackingNumbers: 'Call Tracking Numbers',
      localNumbers: 'Local Numbers',
      tollFreeNumbers: 'Toll Free Numbers',
      messages: 'Messages',
    },
    filters: {
      source: 'Source',
      dates: 'Dates',
      summaryMonth: 'Summary Month',
      showRange: 'Show Range',
      pickDateRange: 'Pick Date Range to Filter',
      select: 'Select',
      none: 'None',
      last7Days: 'Last 7 Days',
      last14Days: 'Last 14 Days',
      last30Days: 'Last 30 Days',
      last60Days: 'Last 60 Days',
      last90Days: 'Last 90 Days',
      last6Months: 'Last 6 Months',
      last12Months: 'Last 12 Months',
      customRange: 'Custom Range',
    },
    logStatuses: {
      completedCalls: 'Completed (Calls)',
      machineCompletedCalls: 'Machine Completed (Calls)',
      noAnswerCalls: 'No Answer (Calls)',
      canceledCalls: 'Cancelled (Calls)',
      failedCalls: 'Failed (Calls)',
      busyCalls: 'Busy (Calls)',
      ivrAnswered: 'IVR Hangup',
      ivrTimeout: 'IVR Timeout',
      deliveredMessages: 'Delivered (Messages)',
      receivedMessages: 'Received (Messages)',
      undeliveredMessages: 'Undelivered (Messages)',
    },
    fallback: {
      unknown: 'Unknown',
      cityUnknown: 'City Unknown',
      unspecified: 'Unspecified',
      notSyndicated: 'Not Syndicated',
      noIVRSelection: 'No IVR Selection',
      phoneCallAnsweredBy: 'Phone call answered by {answeredBy}',
    },
    tooltip: {
      noAssociatedRLS: 'This number has no associated Rental Listing Sites.',
      propertyPhoneNumber: 'Property Phone #',
      customPhoneNumber: 'Custom Phone #',
    },
    answeredBy: {
      human: 'Human',
      machine: 'Machine',
      unknown: 'Unknown',
    },
    log: {
      details: 'Log Details',
      type: 'Log Type:',
      dateTime: 'Date/Time:',
      status: 'Status:',
      callerNumber: 'Caller #:',
      trackingNumber: 'Tracking #:',
      forwardedNumber: 'Forwarded #:',
      callerName: 'Caller Name:',
      callerCity: 'Caller City:',
      property: 'Property:',
      content: 'Content:',
      noContentAvailable: 'No content available!',
      answered: 'Answered:',
      download: 'Download',
      play: 'Play',
    },
    settings: {
      recordingDisclaimer: 'Recording Disclaimer',
      recordingDisclaimerFr: 'Recording Disclaimer (French)',
      playsBeforeEnglish: 'Plays before a caller is connected. Used for non-IVR numbers and IVRs set to English.',
      playsBeforeAll: 'The following message will be played before each call is connected',
      playsBeforeFrench: 'Plays before a caller is connected when an IVR is set to French.',
      currentDisclaimer: 'Current Disclaimer:',
      currentDisclaimerFr: 'Current Disclaimer (French):',
      editDisclaimer: 'Edit Disclaimer',
      editDisclaimerFr: 'Edit Disclaimer (FR)',
      defaultBadge: 'Default',
      textMessageSettings: 'Text Message Settings',
      textMessageSubtitle: 'Control and customize text message auto responses via text message and email',
      customAutoResponse: 'Custom Text Message Auto Response',
      customAutoResponseDescription: 'Customize an immediate, automatic text response to prospects who send a text to your call tracking lines. Your building managers will not be notified.',
      defaultSmsText: 'Default: "Thank you for your message. We will be in touch shortly."',
      smsPlaceholder: 'Thank you for your message. We will be in touch shortly.',
      convertSmsToEmail: 'Convert SMS to Email',
      convertSmsToEmailDescription: 'Convert text messages you receive from a prospect on your call tracking lines to an email, which is immediately send to your building managers. Your building managers can then contact the prospect in order to address the inquiry.',
      saveTextSettings: 'Save Text Settings',
    },
    destination: {
      aiAgent: 'AI leasing agent',
      callGroup: 'Call group: {name}',
      fallback: 'Fallback: {phone} ({type})',
      property: 'Property',
      custom: 'Custom',
    },
    nonSyndicated: {
      infoMessage: 'These are Call Tracking numbers that are attached to a property or a Rental Listing Site but do not have Listing Syndication enabled, or do not have any available units, and therefore will not be displayed on Rental Listings.',
    },
    modal: {
      filters: 'Filters',
    },
    exportMessages: {
      activeNumberSummary: 'Generating active number summary export',
      forwardingNumbers: 'Generating forwarding numbers export',
      callLogs: 'Generating Call Logs export',
      messageLogs: 'Generating Call Messages export',
    },
    recording: {
      pleaseConfirm: 'Please Confirm',
      policyMessage1: 'Rentsync has provided a default recorded disclaimer for this use case.',
      policyMessage2: 'A recording disclaimer is required and cannot be left blank.',
      policyMessage3: 'Please review this message with your legal or compliance advisors to confirm it is appropriate for your business.',
      policyMessage4: 'You are responsible for the disclaimer used by your business and for compliance with any applicable requirements related to recorded messages and information collection.',
      iAgree: 'I Agree',
      useDefaultMessage: 'Use Default Message',
      textToSpeech: 'Text to Speech',
      personalizedRecording: 'Personalized Recording',
      theFollowingMessage: 'The following message will be translated to speech before each call is connected.',
    },
    single: {
      detailsHeading: 'Details',
      advertisingSourcesHeading: 'Advertising Sources',
      multiplePropertiesWarning: 'You have multiple properties tied to this number. Please contact support to update this.',
      ivrBanner: {
        activeTitle: 'Interactive Voice Response (IVR) is active on this number.',
        controlledBy: 'This number\'s routing is currently controlled by {buildingName} IVR',
        viewIvr: 'View IVR',
        forwardingTooltip: 'These forwarding settings won\'t be used while the IVR is active. They\'ll apply if the IVR is disabled.',
      },
      routing: {
        routeCallsTo: 'Route calls to',
        kinds: {
          forwarding: 'Forwarding number',
          ivr: 'Property IVR',
          ivrNamed: 'Property IVR ({ivrName})',
          callGroup: 'Call group',
          aiAgent: 'AI leasing agent',
        },
        callersHearMenu: 'Callers hear the property\'s phone menu.',
        callGroupPicker: 'Call group',
        callGroupOption: '{name} ({count} entry) | {name} ({count} entries)',
        noCallGroupChosen: 'No call group chosen',
        usedOnPropertySection: 'Used on this property',
        otherCallGroupsSection: 'Other call groups',
        manageCallGroups: 'Manage call groups',
        agentSettings: 'Agent settings',
        alwaysRecorded: 'Calls on this route are always recorded.',
        ivrBypassed: 'The property IVR is bypassed for this number.',
        fallbackToPropertyPhone: 'Fall back to property phone',
        fallbackNumber: 'Fallback',
        problems: {
          noProperty: 'This number is routed to the AI leasing agent, but it has no property, so calls go to the fallback. Choose another route before saving.',
          productMissing: 'This property no longer has the AI leasing agent, so calls go to the fallback. Choose another route before saving.',
          notVisible: 'This number is routed to a call group you can\'t see. Choose another route before saving.',
          groupNoProperty: 'This number is routed to a call group that includes the AI leasing agent, which needs a property. Choose another route or group before saving.',
          groupProductMissing: 'This number is routed to a call group that includes the AI leasing agent, which this property doesn\'t have. Choose another route or group before saving.',
          noGroupSelected: 'Choose a call group before saving.',
          noEnabledEntries: 'This call group has no enabled entries, so calls go straight to the fallback.',
        },
      },
      form: {
        callTrackingNumber: 'Call Tracking #',
        forwardingNumber: 'Forwarding',
        property: 'Property',
        callRecording: 'Call Recording?',
        forwardToPropertyPhoneNumber: 'Forward to Property Phone Number',
        customPhoneNumber: 'Custom Phone Number',
        label: 'Label',
        sendTextAutoResponse: 'Send Text Message Auto Response?',
        sendTextAutoResponseOn: 'On',
        sendTextAutoResponseOff: 'Off',
      },
      modals: {
        inputMissing: {
          title: 'Input missing',
          takeMeThere: 'Take me there',
          forwardingRequired: 'A mandatory field in the details section "Forwarding #" is not filled out. Please provide an input before saving.',
          propertyPhoneRequired: 'The Property Details section "Phone" is not filled out. Please provide an input before saving.',
          companyPhoneRequired: 'We noticed you have also not filled out the "Phone" field in the Company Profile section. Please provide an input.',
          goToCompanyProfile: 'Go to Company Profile',
        },
      },
      toast: {
        validationError: 'You have some errors! Please fix before saving.',
        saved: 'Call Tracking # details saved!',
      },
    },
  },
} as const;

export const callTrackingFr = {
  callTracking: {
    stats: {
      calls: 'Appels',
      textMessagesReceived: 'Messages texte reçus',
      avgCallTime: 'Durée moy. des appels',
      callTrackingNumbers: 'Numéros de suivi des appels',
      localNumbers: 'Numéros locaux',
      tollFreeNumbers: 'Numéros sans frais',
      messages: 'Messages',
    },
    filters: {
      source: 'Source',
      dates: 'Dates',
      summaryMonth: 'Mois du sommaire',
      showRange: 'Afficher la plage',
      pickDateRange: 'Choisir une plage de dates pour filtrer',
      select: 'Sélectionner',
      none: 'Aucun',
      last7Days: 'Les 7 derniers jours',
      last14Days: 'Les 14 derniers jours',
      last30Days: 'Les 30 derniers jours',
      last60Days: 'Les 60 derniers jours',
      last90Days: 'Les 90 derniers jours',
      last6Months: 'Les 6 derniers mois',
      last12Months: 'Les 12 derniers mois',
      customRange: 'Plage personnalisée',
    },
    logStatuses: {
      completedCalls: 'Complété (Appels)',
      machineCompletedCalls: 'Complété par machine (Appels)',
      noAnswerCalls: 'Aucune réponse (Appels)',
      canceledCalls: 'Annulé (Appels)',
      failedCalls: 'Échoué (Appels)',
      busyCalls: 'Occupé (Appels)',
      ivrAnswered: 'Raccrochage RVI',
      ivrTimeout: 'Délai RVI',
      deliveredMessages: 'Livré (Messages)',
      receivedMessages: 'Reçu (Messages)',
      undeliveredMessages: 'Non livré (Messages)',
    },
    fallback: {
      unknown: 'Inconnu',
      cityUnknown: 'Ville inconnue',
      unspecified: 'Non spécifié',
      notSyndicated: 'Non syndiqué',
      noIVRSelection: 'Aucune sélection RVI',
      phoneCallAnsweredBy: 'Appel répondu par {answeredBy}',
    },
    tooltip: {
      noAssociatedRLS: 'Ce numéro n\'est associé à aucun site d\'annonces locatives.',
      propertyPhoneNumber: 'Tél. de la propriété',
      customPhoneNumber: 'Numéro personnalisé',
    },
    answeredBy: {
      human: 'Humain',
      machine: 'Machine',
      unknown: 'Inconnu',
    },
    log: {
      details: 'Détails du journal',
      type: 'Type de journal :',
      dateTime: 'Date/Heure :',
      status: 'Statut :',
      callerNumber: 'N° de l\'appelant :',
      trackingNumber: 'N° de suivi :',
      forwardedNumber: 'N° transféré :',
      callerName: 'Nom de l\'appelant :',
      callerCity: 'Ville de l\'appelant :',
      property: 'Propriété :',
      content: 'Contenu :',
      noContentAvailable: 'Aucun contenu disponible !',
      answered: 'Répondu :',
      download: 'Télécharger',
      play: 'Lecture',
    },
    settings: {
      recordingDisclaimer: 'Avis d\'enregistrement',
      recordingDisclaimerFr: 'Avis d\'enregistrement (Français)',
      playsBeforeEnglish: 'Diffusé avant la connexion d\'un appelant. Utilisé pour les numéros sans RVI et les RVI configurés en anglais.',
      playsBeforeAll: 'Le message suivant sera diffusé avant chaque connexion d\'appel',
      playsBeforeFrench: 'Diffusé avant la connexion d\'un appelant lorsqu\'un RVI est configuré en français.',
      currentDisclaimer: 'Avis actuel :',
      currentDisclaimerFr: 'Avis actuel (Français) :',
      editDisclaimer: 'Modifier l\'avis',
      editDisclaimerFr: 'Modifier l\'avis (FR)',
      defaultBadge: 'Par défaut',
      textMessageSettings: 'Paramètres des messages texte',
      textMessageSubtitle: 'Contrôler et personnaliser les réponses automatiques aux messages texte par message texte et par courriel',
      customAutoResponse: 'Réponse automatique personnalisée par message texte',
      customAutoResponseDescription: 'Personnalisez une réponse texte automatique et immédiate aux prospects qui envoient un message à vos lignes de suivi des appels. Vos gestionnaires d\'immeuble ne seront pas notifiés.',
      defaultSmsText: 'Par défaut : « Merci pour votre message. Nous vous contacterons sous peu. »',
      smsPlaceholder: 'Merci pour votre message. Nous vous contacterons sous peu.',
      convertSmsToEmail: 'Convertir les SMS en courriel',
      convertSmsToEmailDescription: 'Convertissez les messages texte reçus de prospects sur vos lignes de suivi des appels en courriels envoyés immédiatement à vos gestionnaires d\'immeuble. Ces derniers peuvent ensuite contacter le prospect pour répondre à sa demande.',
      saveTextSettings: 'Enregistrer les paramètres de messagerie',
    },
    destination: {
      aiAgent: 'Agent de location IA',
      callGroup: 'Groupe d\'appels : {name}',
      fallback: 'Secours : {phone} ({type})',
      property: 'Propriété',
      custom: 'Personnalisé',
    },
    nonSyndicated: {
      infoMessage: 'Ces numéros de suivi des appels sont associés à une propriété ou à un site d\'annonces locatives, mais la syndication d\'annonces n\'est pas activée, ou il n\'y a aucune unité disponible. Par conséquent, ils ne seront pas affichés dans les annonces locatives.',
    },
    modal: {
      filters: 'Filtres',
    },
    exportMessages: {
      activeNumberSummary: 'Génération de l\'export du sommaire des numéros actifs',
      forwardingNumbers: 'Génération de l\'export des numéros de transfert',
      callLogs: 'Génération de l\'export des journaux d\'appels',
      messageLogs: 'Génération de l\'export des journaux de messages',
    },
    recording: {
      pleaseConfirm: 'Veuillez confirmer',
      policyMessage1: 'Rentsync a fourni un avis d\'enregistrement par défaut pour ce cas d\'utilisation.',
      policyMessage2: 'Un avis d\'enregistrement est obligatoire et ne peut pas être laissé vide.',
      policyMessage3: 'Veuillez examiner ce message avec vos conseillers juridiques ou en conformité pour confirmer qu\'il convient à votre entreprise.',
      policyMessage4: 'Vous êtes responsable de l\'avis utilisé par votre entreprise et du respect de toute exigence applicable relative aux messages enregistrés et à la collecte d\'informations.',
      iAgree: 'J\'accepte',
      useDefaultMessage: 'Utiliser le message par défaut',
      textToSpeech: 'Synthèse vocale',
      personalizedRecording: 'Enregistrement personnalisé',
      theFollowingMessage: 'Le message suivant sera converti en parole avant chaque connexion d\'appel.',
    },
    single: {
      detailsHeading: 'Détails',
      advertisingSourcesHeading: 'Sources publicitaires',
      multiplePropertiesWarning: 'Plusieurs propriétés sont liées à ce numéro. Veuillez contacter le support pour effectuer cette mise à jour.',
      ivrBanner: {
        activeTitle: 'La réponse vocale interactive (RVI) est active sur ce numéro.',
        controlledBy: 'Le routage de ce numéro est actuellement contrôlé par le RVI de {buildingName}',
        viewIvr: 'Voir RVI',
        forwardingTooltip: 'Ces paramètres de transfert ne seront pas utilisés tant que le RVI est actif. Ils s\'appliqueront si le RVI est désactivé.',
      },
      routing: {
        routeCallsTo: 'Acheminer les appels vers',
        kinds: {
          forwarding: 'Numéro de transfert',
          ivr: 'RVI de la propriété',
          ivrNamed: 'RVI de la propriété ({ivrName})',
          callGroup: 'Groupe d\'appels',
          aiAgent: 'Agent de location IA',
        },
        callersHearMenu: 'Les appelants entendent le menu téléphonique de la propriété.',
        callGroupPicker: 'Groupe d\'appels',
        callGroupOption: '{name} ({count} entrée) | {name} ({count} entrées)',
        noCallGroupChosen: 'Aucun groupe d\'appels choisi',
        usedOnPropertySection: 'Utilisés sur cette propriété',
        otherCallGroupsSection: 'Autres groupes d\'appels',
        manageCallGroups: 'Gérer les groupes d\'appels',
        agentSettings: 'Paramètres de l\'agent',
        alwaysRecorded: 'Les appels acheminés ainsi sont toujours enregistrés.',
        ivrBypassed: 'Le RVI de la propriété est contourné pour ce numéro.',
        fallbackToPropertyPhone: 'Numéro de la propriété en secours',
        fallbackNumber: 'Secours',
        problems: {
          noProperty: 'Ce numéro est acheminé vers l\'agent de location IA, mais il n\'a pas de propriété. Les appels vont donc au numéro de secours. Choisissez un autre acheminement avant d\'enregistrer.',
          productMissing: 'Cette propriété n\'a plus l\'agent de location IA. Les appels vont donc au numéro de secours. Choisissez un autre acheminement avant d\'enregistrer.',
          notVisible: 'Ce numéro est acheminé vers un groupe d\'appels que vous ne pouvez pas voir. Choisissez un autre acheminement avant d\'enregistrer.',
          groupNoProperty: 'Ce numéro est acheminé vers un groupe d\'appels qui inclut l\'agent de location IA, ce qui exige une propriété. Choisissez un autre acheminement ou groupe avant d\'enregistrer.',
          groupProductMissing: 'Ce numéro est acheminé vers un groupe d\'appels qui inclut l\'agent de location IA, que cette propriété n\'a pas. Choisissez un autre acheminement ou groupe avant d\'enregistrer.',
          noGroupSelected: 'Choisissez un groupe d\'appels avant d\'enregistrer.',
          noEnabledEntries: 'Ce groupe d\'appels n\'a aucune entrée activée. Les appels vont donc directement au numéro de secours.',
        },
      },
      form: {
        callTrackingNumber: 'N° de suivi des appels',
        forwardingNumber: 'Transfert',
        property: 'Propriété',
        callRecording: 'Enregistrement des appels?',
        forwardToPropertyPhoneNumber: 'Transférer vers le numéro de téléphone de la propriété',
        customPhoneNumber: 'Numéro de téléphone personnalisé',
        label: 'Étiquette',
        sendTextAutoResponse: 'Envoyer une réponse automatique par message texte?',
        sendTextAutoResponseOn: 'Activé',
        sendTextAutoResponseOff: 'Désactivé',
      },
      modals: {
        inputMissing: {
          title: 'Saisie manquante',
          takeMeThere: 'M\'y emmener',
          forwardingRequired: 'Un champ obligatoire dans la section des détails « N° de transfert » n\'est pas rempli. Veuillez fournir une valeur avant d\'enregistrer.',
          propertyPhoneRequired: 'Le champ « Téléphone » dans la section Détails de la propriété n\'est pas rempli. Veuillez fournir une valeur avant d\'enregistrer.',
          companyPhoneRequired: 'Nous avons également remarqué que le champ « Téléphone » dans la section Profil de l\'entreprise n\'est pas rempli. Veuillez fournir une valeur.',
          goToCompanyProfile: 'Aller au profil de l\'entreprise',
        },
      },
      toast: {
        validationError: 'Certaines erreurs sont présentes. Veuillez les corriger avant d\'enregistrer.',
        saved: 'Détails du numéro de suivi des appels enregistrés!',
      },
    },
  },
} as const;
